import React, { useMemo, useState } from "react";
import { DocumentMetadata } from "@neum/frontend-shared/types/metagraphTypes";
import { classNames } from "../../../../utils/ClassUtils";
import { Spinner } from "../../../../utils/Spinner";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import EditableLabel from "../../../common/EditableLabel";
import { timestampToHumanReadable } from "../../../../utils/TimeUtils";
import VetRecButton from "../../../common/VetRecButton";
import { getFirstAlphanumericCharacter } from "@neum/frontend-shared/utils/textUtils";

interface MultiDocSelectorTabProps {
    document: DocumentMetadata;
    activeDocument: DocumentMetadata | undefined;
    tabsDisabled: boolean | undefined;
    onSelectedDocChange: (documentId: string) => Promise<void>;
    onSaveDocumentTitle: (newTitle: string) => Promise<void>;
}

const MultiDocSelectorTab: React.FC<MultiDocSelectorTabProps> = ({
    document,
    activeDocument,
    tabsDisabled,
    onSelectedDocChange,
    onSaveDocumentTitle,
}: MultiDocSelectorTabProps) => {
    const isActive = useMemo(() => document === activeDocument, [document, activeDocument]);

    const [isEditing, setIsEditing] = useState(false);

    const isProcessing = useMemo(
        () => ["Processing", "Streaming"].includes(document.status),
        [document.status]
    );

    const isFailed = useMemo(
        () => document.status === "Failed",
        [document.status]
    );

    const isSuccess = useMemo(
        () => !isProcessing && !isFailed,
        [isProcessing, isFailed]
    )

    return (
        <VetRecButton
            disabled={tabsDisabled}
            onClick={() => onSelectedDocChange(document.id)}
            className={classNames(
                isActive
                    ? "border-main-lighter text-main"
                    : "border-transparent text-gray-500 hover:border-gray-400 hover:text-gray-700",
                "group mb-1 inline-flex items-center border-b-2 py-2 px-3 text-sm font-medium cursor-pointer transition-colors duration-200 ease-in-out min-w-[90px]",
                "flex-shrink-0"
        )}
            aria-current={isActive ? "page" : undefined}
        >
            <div className="flex items-center gap-x-3 w-full justify-center">
                {/* Fixed-size container for all icon types */}
                <div className="w-6 h-6 flex items-center justify-center shrink-0">
                    {isProcessing && (
                        <Spinner size="h-5 w-5" timer={true} />
                    )}
                    {isFailed && (
                        <ExclamationCircleIcon
                            className={`h-6 w-6 text-red-400`}
                        />
                    )}
                    {isSuccess && (
                        <div
                            className={`h-6 w-6 flex rounded-full bg-main items-center justify-center`}
                        >
                            <span
                                className={`text-center text-main-text text-xs leading-none`}
                            >
                                {getFirstAlphanumericCharacter(document.title)}
                            </span>
                        </div>
                    )}
                </div>
                <div className="h-10 justify-center items-start flex flex-col gap-y-1">
                    <EditableLabel
                        text={document.title}
                        onSave={onSaveDocumentTitle}
                        className={`${isActive ? "font-semibold" : ""} text-gray-800 whitespace-nowrap`}
                        textInputPlaceholder="Enter title..."
                        labelPlaceholder="Untitled"
                        disabled={!isActive || tabsDisabled || isProcessing}
                        onEditingChange={setIsEditing}
                    />
                    {!isEditing && document.create_timestamp && <span className="text-gray-500 text-xs">{timestampToHumanReadable(document.create_timestamp)}</span>}
                </div>
            </div>
        </VetRecButton>
    );
};

export default MultiDocSelectorTab;
